/**
 * Capa fina sobre el DOM de XML.
 *
 * Se usa @xmldom/xmldom en todas partes (navegador y Node) a proposito: asi
 * las pruebas y la aplicacion se comportan igual, sin depender de las
 * diferencias del DOMParser nativo de cada motor.
 */
import { DOMParser, XMLSerializer } from "@xmldom/xmldom";

export type Elem = Element;

/** Parsea un documento XML. Lanza si esta mal formado. */
export function parsearXml(texto: string): Document {
  const errores: string[] = [];
  const doc = new DOMParser({
    onError: (nivel, msg) => {
      // xmldom avisa de rarezas sin lanzar; convertimos los errores en fallo.
      if (nivel === "error" || nivel === "fatalError") errores.push(String(msg));
    },
  }).parseFromString(texto, "text/xml") as unknown as Document;

  if (errores.length) {
    throw new Error("XML mal formado:\n" + errores.slice(0, 5).join("\n"));
  }
  if (!doc.documentElement) throw new Error("XML sin elemento raiz");
  return doc;
}

/** Vuelve a texto un nodo completo, con sus atributos y descendencia. */
export function serializarNodo(n: Node): string {
  return new XMLSerializer().serializeToString(n as never);
}

/** Los hijos que son elementos, en orden. */
export function hijosElem(el: Elem): Elem[] {
  const out: Elem[] = [];
  for (let n = el.firstChild; n; n = n.nextSibling) {
    if (n.nodeType === 1) out.push(n as Elem);
  }
  return out;
}

/** El nombre local de un elemento, sin prefijo de espacio de nombres. */
export function nombreLocal(el: Elem): string {
  return el.localName ?? el.nodeName.replace(/^.*:/, "");
}

/** El primer hijo con ese nombre local, si existe. */
export function hijo(el: Elem, nombre: string): Elem | undefined {
  for (let n = el.firstChild; n; n = n.nextSibling) {
    if (n.nodeType === 1 && nombreLocal(n as Elem) === nombre) return n as Elem;
  }
  return undefined;
}

/** Texto de un hijo, recortado. undefined si no existe o esta vacio. */
export function textoHijo(el: Elem, nombre: string): string | undefined {
  const h = hijo(el, nombre);
  if (!h) return undefined;
  const t = (h.textContent ?? "").trim();
  return t === "" ? undefined : t;
}

/** Todos los atributos de un elemento como objeto llano. */
export function todosAtribs(el: Elem): Record<string, string> {
  const out: Record<string, string> = {};
  const as = el.attributes;
  for (let i = 0; i < as.length; i++) {
    const a = as.item(i);
    if (a) out[a.name] = a.value;
  }
  return out;
}

/**
 * Los atributos que NO estan en la lista de conocidos.
 * Sirve para no perder atributos que no modelamos.
 */
export function atribsRestantes(
  el: Elem,
  conocidos: readonly string[],
): Record<string, string> {
  const out: Record<string, string> = {};
  const as = el.attributes;
  for (let i = 0; i < as.length; i++) {
    const a = as.item(i);
    if (a && !conocidos.includes(a.name)) out[a.name] = a.value;
  }
  return out;
}

/** Atributo, o undefined si falta o esta vacio. */
export function atrib(el: Elem, nombre: string): string | undefined {
  const v = el.getAttribute(nombre);
  return v === null || v === "" ? undefined : v;
}
